import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { Play, Volume2, VolumeX } from "lucide-react";

const IMAGE_AUTOPLAY_MS = 5000;
const VIDEO_MAX_MS = 30000;

const apiUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

/**
 * Resolve the destination URL for a slide. Per-slide action overrides
 * the banner-level action; falls back to the parent banner action.
 */
function destinationFor(slide, banner) {
  const at = slide.action_type || banner.action_type;
  const av = slide.action_value || banner.action_value;
  if (!at || !av) return null;
  if (at === "external_link") return av;
  if (at === "open_whatsapp") return `https://wa.me/${(av || "").replace(/\D/g, "")}`;
  if (at === "open_profile") return `/places/${av}`;
  if (at === "open_vip") return `/places/${av}?vip=1`;
  return av || null;
}

/**
 * Multi-slide banner carousel supporting image / YouTube / MP4 slides.
 * Per spec: muted autoplay, loop, hidden controls, lazy loading (only
 * active slide gets media), auto-advance pauses on video slides until
 * the video ends OR 30s elapse — whichever happens first.
 */
export default function BannerCarousel({ banner, placement }) {
  const slides = useMemo(() => (banner?.slides?.length ? banner.slides : []), [banner]);
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, dragFree: false });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setActiveIndex(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  // Auto-advance — image slides advance after 5 s; video slides handle their own end-of-play
  // (or 30 s cap). We expose `advance` via context-free callback passed into each slide.
  const autoplayRef = useRef(null);
  const advance = useCallback(() => {
    if (!embla) return;
    embla.scrollNext();
  }, [embla]);

  useEffect(() => {
    if (!embla || slides.length <= 1) return;
    clearTimeout(autoplayRef.current);
    const active = slides[activeIndex];
    if (active?.media_type === "image") {
      autoplayRef.current = setTimeout(advance, IMAGE_AUTOPLAY_MS);
    }
    // YouTube / MP4 slides drive their own advance via onEnded / 30s cap
    return () => clearTimeout(autoplayRef.current);
  }, [embla, slides, activeIndex, advance]);

  if (!slides.length) return null;

  return (
    <section
      className="relative w-full overflow-hidden"
      data-testid={`banner-${placement}`}
    >
      <div ref={emblaRef} className="overflow-hidden" data-testid={`banner-${placement}-viewport`}>
        <div className="flex">
          {slides.map((slide, idx) => (
            <div
              key={slide.id || idx}
              className="relative flex-[0_0_100%] min-w-0"
              data-testid={`banner-slide-${placement}-${idx}`}
            >
              <BannerSlide
                slide={slide}
                banner={banner}
                active={idx === activeIndex}
                onEnded={advance}
                placement={placement}
                index={idx}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10" data-testid={`banner-${placement}-dots`}>
          {slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`banner-dot-${placement}-${i}`}
              onClick={() => embla?.scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === activeIndex ? "w-6 bg-gold" : "w-1.5 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function BannerSlide({ slide, banner, active, onEnded, placement, index }) {
  const dest = destinationFor(slide, banner);
  const title = slide.title || banner.title;
  const cta = slide.cta_label || banner.cta_label;
  const isExternal = (slide.action_type || banner.action_type) === "external_link" ||
                     (slide.action_type || banner.action_type) === "open_whatsapp";

  const inner = (() => {
    if (slide.media_type === "youtube") {
      return <YouTubeSlide slide={slide} active={active} onEnded={onEnded} placement={placement} index={index} />;
    }
    if (slide.media_type === "mp4") {
      return <Mp4Slide slide={slide} active={active} onEnded={onEnded} placement={placement} index={index} />;
    }
    return <ImageSlide slide={slide} placement={placement} index={index} />;
  })();

  // Wrap in link only when destination exists. Use a div otherwise so videos can be tapped to unmute.
  const wrapperCls = "block relative w-full aspect-[16/9] sm:aspect-[21/9] bg-[#0a0a0a]";

  // Caption overlay (title + CTA pill) on bottom-left
  const overlay = title ? (
    <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 pointer-events-none bg-gradient-to-t from-black/85 via-black/40 to-transparent z-[2]">
      <div className="font-display text-xl sm:text-2xl text-white leading-tight max-w-[80%] [text-shadow:0_2px_12px_rgba(0,0,0,0.7)]" data-testid={`banner-slide-title-${placement}-${index}`}>
        {title}
      </div>
      {cta && (
        <span className="inline-block mt-2 px-3 py-1 rounded-full bg-gold text-[#0a0a0a] text-xs font-medium uppercase tracking-widest" data-testid={`banner-slide-cta-${placement}-${index}`}>
          {cta}
        </span>
      )}
    </div>
  ) : null;

  if (dest && isExternal) {
    return (
      <a href={dest} target="_blank" rel="noopener noreferrer" className={wrapperCls} data-testid={`banner-link-${placement}-${index}`}>
        {inner}{overlay}
      </a>
    );
  }
  if (dest) {
    return (
      <Link to={dest} className={wrapperCls} data-testid={`banner-link-${placement}-${index}`}>
        {inner}{overlay}
      </Link>
    );
  }
  return <div className={wrapperCls} data-testid={`banner-link-${placement}-${index}`}>{inner}{overlay}</div>;
}

function ImageSlide({ slide, placement, index }) {
  const url = apiUrl(slide.media_url);
  if (!url) return <div className="absolute inset-0 bg-[#171717]" />;
  return (
    <img
      src={url}
      alt={slide.title || ""}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
      data-testid={`banner-img-${placement}-${index}`}
    />
  );
}

function Mp4Slide({ slide, active, onEnded, placement, index }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const url = apiUrl(slide.media_url);
  const poster = apiUrl(slide.poster_url);
  const capRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active && url) {
      if (v.src !== url) {
        v.src = url;
        try { v.load(); } catch { /* noop */ }
      }
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked — poster stays */ });
      clearTimeout(capRef.current);
      capRef.current = setTimeout(() => { onEnded?.(); }, VIDEO_MAX_MS);
    } else {
      try { v.pause(); } catch { /* noop */ }
      clearTimeout(capRef.current);
    }
    return () => clearTimeout(capRef.current);
  }, [active, url, onEnded]);

  if (!url) {
    return poster ? (
      <img src={poster} alt={slide.title || ""} className="absolute inset-0 w-full h-full object-cover" data-testid={`banner-mp4-fallback-${placement}-${index}`} />
    ) : (
      <div className="absolute inset-0 bg-[#171717] grid place-items-center text-foreground/40" data-testid={`banner-mp4-fallback-${placement}-${index}`}>
        <Play className="w-6 h-6" strokeWidth={1.5}/>
      </div>
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        poster={poster || undefined}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onEnded={() => onEnded?.()}
        className="absolute inset-0 w-full h-full object-cover bg-black"
        data-testid={`banner-mp4-${placement}-${index}`}
      />
      <MuteButton
        muted={muted}
        onToggle={(e) => { e.preventDefault(); e.stopPropagation(); setMuted((m) => !m); }}
        testid={`banner-mute-${placement}-${index}`}
      />
    </>
  );
}

function YouTubeSlide({ slide, active, onEnded, placement, index }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [errored, setErrored] = useState(false);
  const capRef = useRef(null);
  const poster = apiUrl(slide.poster_url) || `https://i.ytimg.com/vi/${slide.youtube_id}/hqdefault.jpg`;

  // Lazy-load YT IFrame API only when first YouTube slide becomes active
  useEffect(() => {
    if (!active) return;
    if (!slide.youtube_id) { setErrored(true); return; }
    let cancelled = false;

    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) { resolve(window.YT); return; }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev && prev();
        resolve(window.YT);
      };
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        s.async = true;
        s.onerror = () => { if (!cancelled) setErrored(true); };
        document.head.appendChild(s);
      }
    });

    (async () => {
      try {
        const YT = await ensureApi();
        if (cancelled || !containerRef.current) return;
        playerRef.current = new YT.Player(containerRef.current, {
          videoId: slide.youtube_id,
          playerVars: {
            autoplay: 1,
            mute: 1,
            loop: 1,
            controls: 0,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            playlist: slide.youtube_id, // required for `loop=1`
            iv_load_policy: 3,
            disablekb: 1,
          },
          events: {
            onReady: (ev) => {
              try { ev.target.mute(); ev.target.playVideo(); } catch {/* noop */}
            },
            onStateChange: (ev) => {
              if (ev.data === YT.PlayerState.ENDED) onEnded?.();
            },
            onError: () => setErrored(true),
          },
        });
        clearTimeout(capRef.current);
        capRef.current = setTimeout(() => { onEnded?.(); }, VIDEO_MAX_MS);
      } catch {
        setErrored(true);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(capRef.current);
      try { playerRef.current?.destroy?.(); } catch {/* noop */}
      playerRef.current = null;
    };
  }, [active, slide.youtube_id, onEnded]);

  // Mute / unmute via YT API
  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const p = playerRef.current;
    if (!p) return;
    if (muted) { try { p.unMute(); } catch {/* noop */} setMuted(false); }
    else { try { p.mute(); } catch {/* noop */} setMuted(true); }
  };

  if (errored) {
    return (
      <img src={poster} alt={slide.title || ""} className="absolute inset-0 w-full h-full object-cover" data-testid={`banner-yt-fallback-${placement}-${index}`} />
    );
  }

  return (
    <>
      {/* Poster shown immediately; iframe replaces it once API mounts */}
      {!active && (
        <img src={poster} alt={slide.title || ""} className="absolute inset-0 w-full h-full object-cover" data-testid={`banner-yt-poster-${placement}-${index}`} />
      )}
      {active && (
        <div className="absolute inset-0 pointer-events-none" data-testid={`banner-yt-${placement}-${index}`}>
          <div ref={containerRef} className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:block" />
        </div>
      )}
      {active && (
        <MuteButton
          muted={muted}
          onToggle={toggleMute}
          testid={`banner-mute-${placement}-${index}`}
        />
      )}
    </>
  );
}

function MuteButton({ muted, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={muted ? "Unmute" : "Mute"}
      data-testid={testid}
      className="absolute top-3 right-3 z-[3] w-11 h-11 grid place-items-center rounded-full bg-black/60 backdrop-blur-sm border border-white/15 text-white hover:text-gold transition-colors"
    >
      {muted ? <VolumeX className="w-4 h-4" strokeWidth={1.5}/> : <Volume2 className="w-4 h-4" strokeWidth={1.5}/>}
    </button>
  );
}
